import { Check, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function ParentInfoPage() {
  const starterPackItems = [
    { name: "A3 Polyholdall", price: "£2.00", description: "For carrying artwork" },
    { name: "A4 Sketchbook", price: "£3.00", description: "Quality sketchbook" },
    { name: "Graphite Pencil Set (6)", price: "£2.00", description: "Box of 6 pencils" },
    { name: "Oil Pastels (16)", price: "£3.50", description: "Set of 16 oil pastels" },
    { name: "Watercolour Set with Brush", price: "£5.00", description: "Complete watercolour set" },
    { name: "Coloured Pencils (12)", price: "£4.00", description: "Set of 12 coloured pencils" },
    { name: "Acrylic Paints (12)", price: "£7.00", description: "Set of 12 acrylic paints" },
    { name: "Eraser", price: "£0.50", description: "Quality eraser" },
    { name: "Sharpener", price: "£0.50", description: "Pencil sharpener" },
    { name: "Brush Set", price: "£7.50", description: "Round and flat brushes" },
    { name: "Pencil Case", price: "£3.20", description: "Durable pencil case" }
  ];

  const totalValue = starterPackItems.reduce((sum, item) => {
    return sum + parseFloat(item.price.replace('£', ''));
  }, 0);

  return (
    <div className="min-h-screen bg-artsy-background">
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-white border-b-2 border-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Parent <span className="text-teal-600">Information</span>
          </h1>
          <p className="font-body text-lg text-slate-600">
            Everything you need to know about Artsy Craftsy After School Art Club.
          </p>
        </div>
      </section>

      {/* About the Club */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card-pricing mb-12">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-6">
              About After School Art Club
            </h2>
            <div className="font-body text-slate-700 space-y-4">
              <p>
                After School Art Club is designed to inspire children in <strong>Years 1–6</strong> to explore their creativity, develop artistic skills, and build confidence in a supportive environment.
              </p>
              <p>
                Each term varies in length and finishes with a <strong>children's art exhibition</strong> where parents are invited to attend and celebrate their children's achievements.
              </p>
              <p>
                Our sessions cover a wide range of techniques including drawing, painting, mixed media, and more. Every child is encouraged to express themselves and explore their unique artistic voice.
              </p>
            </div>
          </div>

          {/* Pricing */}
          <div className="card-sticker mb-12">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-6">
              Fees & Pricing
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Star className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-heading font-bold text-slate-900">£8 per child per session</p>
                    <p className="font-body text-slate-600 text-sm">Standard rate</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Star className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-heading font-bold text-slate-900">£96 per 12-week term</p>
                    <p className="font-body text-slate-600 text-sm">Full term payment</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Star className="w-6 h-6 text-pink-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-heading font-bold text-slate-900">£7 sibling discount</p>
                    <p className="font-body text-slate-600 text-sm">Per session for additional children</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Star className="w-6 h-6 text-pink-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-heading font-bold text-slate-900">£62 returning student</p>
                    <p className="font-body text-slate-600 text-sm">Special rate per term</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Starter Pack */}
          <div className="card-sticker mb-12">
            <div className="flex items-center gap-3 mb-6">
              <h2 className="font-heading text-3xl font-bold text-slate-900">
                Starter Art Pack
              </h2>
              <span className="tag-popular">Worth £{totalValue.toFixed(2)}</span>
            </div>
            <p className="font-body text-slate-600 mb-6">
              The starter art pack is <strong>included in the term fee</strong>. Each child receives these quality materials to use throughout the term and take home:
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              {starterPackItems.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-teal-600" />
                    <span className="font-body text-slate-700">{item.name}</span>
                  </div>
                  <span className="font-heading font-bold text-slate-900">{item.price}</span>
                </div>
              ))}
            </div>
            <p className="font-body text-sm text-slate-500 mt-4">
              Additional materials (heavy cartridge paper, watercolour paper, and pastel paper) will be provided during the term.
            </p>
          </div>

          {/* What to Bring */}
          <div className="card-sticker mb-12">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-6">
              What to Bring
            </h2>
            <ul className="space-y-3 font-body text-slate-700">
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-teal-600" />
                Wear clothes that can get messy (old t-shirt or apron recommended)
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-teal-600" />
                A healthy snack and water bottle
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-teal-600" />
                Their art pack (provided at the start of term)
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-teal-600" />
                Enthusiasm and creativity!
              </li>
            </ul>
          </div>

          {/* End of Term Exhibition */}
          <div className="card-sticker mb-12 border-pink-500">
            <h2 className="font-heading text-3xl font-bold text-pink-500 mb-4">
              End of Term Exhibition
            </h2>
            <p className="font-body text-slate-700">
              Each term concludes with a special <strong>children's art exhibition</strong> where parents and family are invited to attend. It's a wonderful opportunity to celebrate your child's artistic achievements and see their work displayed professionally.
            </p>
          </div>

          {/* Weekly Updates */}
          <div className="card-sticker mb-12 border-teal-600">
            <h2 className="font-heading text-3xl font-bold text-teal-600 mb-4">
              Weekly Updates
            </h2>
            <p className="font-body text-slate-700">
              Parents receive <strong>weekly summaries</strong> about what their child learned in class. These updates are available in your Parent Portal and sent via email when published. Stay connected with your child's creative journey!
            </p>
          </div>

          {/* CTA */}
          <div className="text-center">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Ready to get started?
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register" className="btn-primary" data-testid="info-register">
                Register Now
              </Link>
              <Link to="/contact" className="btn-outline" data-testid="info-contact">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
